import { useQuery } from "@apollo/client";
import { Link } from "react-router-dom";
import {
    GET_RECENT_BLOGS,
    GET_BLOG_CATEGORIES,
    GET_BLOG_TAGS
} from "../../graphql/operations/publicBlog";
import { publicBlogLabel } from "../../data/seo/kw3OnPage";
import { ClockIcon, TagIcon, FolderIcon } from "@heroicons/react/24/outline";

export default function BlogSidebar() {
    const { data: recentData } = useQuery(GET_RECENT_BLOGS, { variables: { limit: 5 } });
    const { data: categoriesData } = useQuery(GET_BLOG_CATEGORIES);
    const { data: tagsData } = useQuery(GET_BLOG_TAGS);

    const categories: string[] = categoriesData?.getBlogCategories ?? [];
    const tags: string[] = tagsData?.getBlogTags ?? [];

    return (
        <aside className="space-y-8">
            {/* Recent Blogs */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <ClockIcon className="h-5 w-5 mr-2 text-orange-600" />
                    Recent Posts
                </h3>
                <div className="space-y-4">
                    {recentData?.getRecentBlogs?.map((blog: any) => (
                        <Link
                            key={blog.id}
                            to={`/blog/${blog.slug}`}
                            className="block group"
                        >
                            <div className="flex gap-3">
                                {blog.coverImage && (
                                    <img
                                        src={blog.coverImage}
                                        alt={publicBlogLabel(blog.slug, blog.title)}
                                        width={64}
                                        height={64}
                                        loading="lazy"
                                        decoding="async"
                                        className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                                    />
                                )}
                                <div className="flex-1 min-w-0">
                                    <h4 className="text-sm font-semibold text-gray-900 group-hover:text-orange-600 transition-colors line-clamp-2">
                                        {publicBlogLabel(blog.slug, blog.title)}
                                    </h4>
                                    <p className="text-xs text-gray-500 mt-1">
                                        {new Date(blog.publishedAt).toLocaleDateString('en-US', {
                                            month: 'short',
                                            day: 'numeric',
                                            year: 'numeric'
                                        })}
                                    </p>
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            </div>

            {categories.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <FolderIcon className="h-5 w-5 mr-2 text-orange-600" />
                        Categories
                    </h3>
                    <div className="flex flex-wrap gap-2">
                        {categories.map((category) => (
                            <Link
                                key={category}
                                to={`/blogs?category=${encodeURIComponent(category)}`}
                                className="inline-flex items-center px-3 py-1.5 bg-orange-50 text-orange-700 text-sm font-medium rounded-full hover:bg-orange-100 transition-colors"
                            >
                                {category}
                            </Link>
                        ))}
                    </div>
                </div>
            )}

            {tags.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <TagIcon className="h-5 w-5 mr-2 text-pink-600" />
                        Tags
                    </h3>
                    <div className="flex flex-wrap gap-2">
                        {tags.map((tag) => (
                            <Link
                                key={tag}
                                to={`/blogs?tag=${encodeURIComponent(tag)}`}
                                className="inline-flex items-center px-3 py-1.5 bg-pink-50 text-pink-700 text-sm font-medium rounded-full hover:bg-pink-100 transition-colors"
                            >
                                #{tag}
                            </Link>
                        ))}
                    </div>
                </div>
            )}
        </aside>
    );
}
